import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { fetchImageTrialState } from "@/services/imageTrial";
import type { ImageTrialStateView } from "@/types/imageTrial";

/**
 * TanStack Query hook for `GET /api/v1/billing/image-trial-state`.
 *
 * Source: PROD-PRICING-I-2 frontend banner ship (ENG-PRICING-WAVE-4
 * sub-issue I-2 outstanding bullets (b) + (c)).
 *
 * Polls every 5 minutes — the underlying counter only mutates on
 * `POST /image-generation` calls, which the user triggers explicitly,
 * so client-side polling is purely a cross-tab consistency safety net.
 *
 * `retry: false` because the endpoint is a read-only state query and
 * any 5xx burst would just hammer the backend without changing the
 * cached value. The banner falls back to "hidden" on error which is
 * the same behavior as `tier !== "starter"` — fail-safe-quiet.
 */
export const IMAGE_TRIAL_QUERY_KEY = ["billing", "image-trial-state"] as const;

export function useImageTrialState(
  enabled: boolean = true,
): UseQueryResult<ImageTrialStateView, Error> {
  return useQuery<ImageTrialStateView, Error>({
    queryKey: IMAGE_TRIAL_QUERY_KEY,
    queryFn: fetchImageTrialState,
    enabled,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}
